/*
 * LUV ABOMINABLE Cart Drawer
 * Client-side rendering + optimistic UI for perceived speed.
 */
(function () {
  'use strict';

  var overlay = document.querySelector('[data-lv-cart]');
  if (!overlay) return;

  // Fallback must match settings.free_shipping_threshold AND the 'Free Hat' discount minimum.
  // These drifted apart before (copy said 325, the live discount required 360), so a shopper
  // was promised something at checkout they did not get. Change them together or not at all.
  // 2026-09-23: moved to 190 (audits/aov_threshold_2026-09-23.png); the offer is the hat only.
  var threshold = parseFloat(overlay.getAttribute('data-free-ship-threshold') || '190');
  var thresholdCents = threshold * 100;
  // Free-gift-with-purchase config (empty variant id disables the feature).
  var giftVariantId = parseInt(overlay.getAttribute('data-free-gift-variant-id') || '', 10) || null;
  // Where gift-line clicks should route (real product PDP, not the hidden $0 one).
  var giftDisplayUrl = overlay.getAttribute('data-free-gift-display-url') || '';
  // Real retail-price cents for the strikethrough (cart line items don't carry compare_at).
  var giftCompareCents = parseInt(overlay.getAttribute('data-free-gift-compare-cents') || '0', 10) || 0;
  var giftAdjustInFlight = false;  // guard against re-firing add/remove while a sync is in flight
  var giftAddFailed = false;       // if the add fails (e.g. 422 out of stock), skip further retries this session
  var cartCache = null;            // last known /cart.js payload
  var qtySyncTimers = {};          // per-line debounce timers
  var pendingKeys = Object.create(null); // line keys with a /cart/change.js in flight
  var pdpAddInFlight = false;      // one PDP /cart/add.js at a time — guards Enter-spam past disabled button
  var drawerOpener = null;         // element that triggered openDrawer — focus returns here on close
  var toast = null;

  // TWO predicates, deliberately. One cannot do both jobs (Codex review 2026-08-14):
  //
  //   isManagedGiftLine  -- "the theme put this here". Requires the EXACT gift variant AND
  //     our own _free_gift marker. Used for add/remove bookkeeping. Property alone is not
  //     enough: a shopper can set any line property via /cart/add.js, so trusting it alone
  //     let ANY paid product render as FREE. Variant alone is not enough either: it would
  //     match a cap the shopper chose to buy, and we must never yank that.
  //
  //   isFreeGiftLine     -- "the SERVER agrees this is free". Managed AND final_line_price
  //     is actually 0. This is the only thing allowed to drive the FREE label, the
  //     strikethrough, and the removal of qty/remove controls. The theme never asserts a
  //     price the cart cannot back.
  function isManagedGiftLine(it) {
    if (!it || !giftVariantId) return false;
    if (it.variant_id !== giftVariantId && it.id !== giftVariantId) return false;
    var v = it.properties && it.properties._free_gift;
    return v === 'true' || v === true;
  }
  function isFreeGiftLine(it) {
    return isManagedGiftLine(it) && (it.final_line_price === 0);
  }
  // Any line carrying the gift variant, however it got there. If the shopper already has a
  // cap in the bag we must not auto-add a second one.
  function hasGiftVariant(cart) {
    if (!cart || !cart.items || !giftVariantId) return false;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.variant_id === giftVariantId || it.id === giftVariantId) return true;
    }
    return false;
  }
  function findGiftLine(cart) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) if (isManagedGiftLine(cart.items[i])) return cart.items[i];
    return null;
  }
  // Any line of the gift variant (paid or free) is excluded from the qualifying subtotal, matching
  // Shopify, which excludes a BXGY "gets" product from its own "buys" subtotal.
  function isGiftVariantLine(it) {
    return !!(it && giftVariantId && (it.variant_id === giftVariantId || it.id === giftVariantId));
  }
  // Qualifying subtotal = original_line_price across NON-gift lines. Using
  // original (pre-discount) means a discount code can't push a qualifying cart
  // below threshold and yank the gift line.
  function qualifyingSubtotal(cart) {
    if (!cart || !cart.items) return 0;
    var sum = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGiftVariantLine(it)) continue;
      sum += (it.original_line_price != null ? it.original_line_price : it.final_line_price) || 0;
    }
    return sum;
  }

  function getToast() {
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'lv-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
    return toast;
  }
  function showToast(msg) {
    var el = getToast();
    el.textContent = msg;
    el.classList.add('show');
    clearTimeout(showToast._t);
    showToast._t = setTimeout(function () { el.classList.remove('show'); }, 2400);
  }
  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function moneyShort(cents) { return '$' + Math.round(cents / 100); }
  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]; }); }

  function openDrawer(trigger) {
    drawerOpener = trigger || document.activeElement || null;
    overlay.classList.remove('hidden');
    requestAnimationFrame(function () {
      overlay.classList.add('open');
      // Move keyboard focus into the drawer for screen readers + Esc users.
      var drawerEl = overlay.querySelector('.lv-cart-drawer');
      if (drawerEl) drawerEl.focus({ preventScroll: true });
    });
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    overlay.classList.remove('open');
    document.body.style.overflow = '';
    setTimeout(function () { overlay.classList.add('hidden'); }, 240);
    // Return focus to the element that opened the drawer (a11y).
    if (drawerOpener && typeof drawerOpener.focus === 'function') {
      drawerOpener.focus();
    }
    drawerOpener = null;
  }

  function updateBagCount(count) {
    document.querySelectorAll('[data-lv-bag-count]').forEach(function (el) { el.textContent = count; });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }
  // HI-03 fix: key the cart line by item.key (variant+properties hash) instead of 1-based line index.
  // Indices shift after a Remove; the line key is stable across mutations, so debounced qty updates
  // can't race a server-side reindex.
  function changeLine(id, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: id, quantity: qty })
    }).then(function (r) { return r.json(); });
  }
  /* ===========================================================
   * ONE ROUND TRIP PER ADD (2026-09-10)
   *
   * Derrick: "make it so things add to cart faster, make sure it's not doing multiple round
   * trips, it was taking the server forever." It was: POST /cart/add.js, then GET /cart.js,
   * then (after the upsell rework) sometimes GET /?sections=lv-cart-drawer. Three requests
   * before the shopper saw a finished drawer, which is why an "adding..." animation existed
   * at all.
   *
   * Shopify's Cart AJAX API renders sections into the mutation response. Measured on this
   * store: /cart/add.js with `sections: 'lv-cart-drawer'` returns the whole drawer already
   * rendered -- line items, ship bar, totals, bag count and upsells -- in 794 ms, one request.
   * (Ruling 2026-08-14: "cart mutations should send sections+sections_url or every add costs
   * 2-3 round trips.")
   *
   * So: paint from the returned HTML, and only touch /cart.js when the free-gift logic
   * genuinely has to act. The section publishes what that decision needs on its root.
   * =========================================================== */
  var SECTION_ID = 'lv-cart-drawer';

  function sectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  function addToCart(variantId, qty) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: qty || 1 }],
        sections: SECTION_ID,
        sections_url: sectionsUrl()
      })
    }).then(function (r) { return r.json(); });
  }

  /* Paint the drawer from a server-rendered section. Returns a summary the gift logic can act
   * on, or null when the payload was not usable (caller then falls back to a /cart.js refresh).
   *
   * Only the INNER regions are swapped, never the overlay itself: every click handler is
   * delegated on `overlay`, so replacing its children keeps them bound with no rebinding. */
  function applyDrawerSection(html) {
    if (!html) return null;
    var doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); }
    catch (e) { return null; }
    var root = doc.querySelector('[data-lv-cart]');
    if (!root) return null;

    var swap = [
      ['[data-lv-cart-items]', true],
      ['.lv-ship-bar', true],
      ['.lv-cart-totals', true],
      ['[data-lv-upsells]', true]
    ];
    for (var i = 0; i < swap.length; i++) {
      var sel = swap[i][0];
      var fresh = root.querySelector(sel);
      var live = overlay.querySelector(sel);
      if (fresh && live) live.innerHTML = fresh.innerHTML;
    }
    // The upsell container also carries the anchor it was rendered for.
    var freshUp = root.querySelector('[data-lv-upsells]');
    var liveUp = overlay.querySelector('[data-lv-upsells]');
    if (freshUp && liveUp) {
      liveUp.setAttribute('data-lv-upsell-anchor', freshUp.getAttribute('data-lv-upsell-anchor') || '');
    }

    var count = parseInt(root.getAttribute('data-lv-item-count'), 10);
    if (!isFinite(count)) count = 0;
    updateBagCount(count);

    var summary = {
      itemCount: count,
      qualifyingCents: parseInt(root.getAttribute('data-lv-qualifying-cents'), 10) || 0,
      hasManagedGift: (root.getAttribute('data-lv-has-gift') || '') === 'true'
    };

    // The section is server truth; anything cached from an older /cart.js is now stale.
    // Null is safe: every consumer guards on it and falls back to DOM-derived values.
    cartCache = null;

    // Re-run the anchor/shrink pass against the freshly painted cards. Handles are read from
    // the rendered lines, so this needs no network.
    reconcileUpsellsFromDom(summary);
    return summary;
  }

  /* The free gift is the ONLY thing that still needs the full cart JSON (it reasons about line
   * properties and server-side discounting). Fetch it only when a gift could actually be owed
   * or retracted -- otherwise the add really is one request. */
  function settleGift(summary) {
    if (!giftVariantId || !summary) return;
    var needs = summary.hasManagedGift || summary.qualifyingCents >= thresholdCents;
    if (!needs) return;
    fetchCart().then(function (cart) { cartCache = cart; syncFreeGift(cart); })
      .catch(function () { /* the drawer is already painted from server truth; leave it */ });
  }
  // Atomic multi-line update. `updates` is { line_key: new_qty }. Returns the
  // fresh cart. Using this instead of two sequential /cart/change.js calls
  // eliminates the race where a customer could hit Checkout with a stale gift
  // line still on the server between mutation #1 and mutation #2.
  function updateCartLines(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  // Predict what qualifyingSubtotal will be after `pending` (map of key -> new qty)
  // is applied server-side. Lets us decide BEFORE the network call whether the
  // customer's mutation will drop them below threshold — so we can batch the
  // gift removal into the same request instead of chasing it after.
  function predictedQualifying(cart, pending) {
    if (!cart || !cart.items) return 0;
    var sum = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGiftVariantLine(it)) continue;
      var newQty = (pending && pending[it.key] != null) ? pending[it.key] : it.quantity;
      var unit = it.quantity ? (it.original_line_price || it.final_line_price || 0) / it.quantity : (it.original_price || 0);
      sum += Math.round(unit * newQty);
    }
    return sum;
  }

  // Checkout gate: any in-flight cart mutation disables the Checkout button so
  // no one can race a stale cart through to checkout with the gift line still
  // present. Cleared when all pending work resolves.
  var pendingMutations = 0;
  function beginMutation() {
    pendingMutations++;
    var btn = overlay.querySelector('.lv-checkout');
    if (btn) { btn.setAttribute('disabled', ''); btn.setAttribute('aria-busy', 'true'); }
  }
  function endMutation() {
    pendingMutations = Math.max(0, pendingMutations - 1);
    if (pendingMutations === 0) {
      var btn = overlay.querySelector('.lv-checkout');
      if (btn) { btn.removeAttribute('disabled'); btn.removeAttribute('aria-busy'); }
    }
  }

  function renderItem(it) {
    var variantParts = (it.options_with_values || []).map(function (o) { return escapeHtml(o.value); }).join(' &middot; ');
    var img = it.featured_image && it.featured_image.url ? it.featured_image.url : (it.image || '');
    var titleEsc = escapeHtml(it.product_title);
    var thumbHtml = img ? '<img src="' + escapeHtml(img) + '" alt="' + titleEsc + '" loading="lazy" width="80" height="100">' : '';
    var variantHtml = variantParts ? '<p class="variant">' + variantParts + '</p>' : '';
    // Server truth: only a line the CART reports at zero may render as FREE. A managed line
    // the server did NOT discount falls through to the normal branch and renders its real
    // price -- and syncFreeGift will retract it. Never print a claim the cart cannot back.
    if (isFreeGiftLine(it)) {
      // Gift line — badge + strikethrough on the real retail price, no qty/remove controls.
      // Anchor routes to the real display product (settings.free_gift_display_handle),
      // not the hidden $0 PDP which 404-redirects.
      // Strikethrough uses giftCompareCents (real product's price from Liquid lookup);
      // cart line items don't expose compare_at_price, so falling back to line_price
      // would show $0 struck through — meaningless.
      var compareCents = giftCompareCents
                        || (it.variant && it.variant.compare_at_price)
                        || (it.original_line_price != null ? it.original_line_price : it.final_line_price) || 0;
      var giftUrl = giftDisplayUrl || it.url;
      return '<article class="lv-cart-item lv-cart-item-gift" data-lv-item data-handle="' + escapeHtml(it.handle || '') + '" data-lv-gift="1" data-key="' + escapeHtml(it.key) + '" data-unit-cents="0" data-line-price="' + (it.final_line_price || 0) + '">'
        + '<a href="' + escapeHtml(giftUrl) + '" class="thumb" aria-label="View ' + titleEsc + '">' + thumbHtml + '</a>'
        + '<div class="info">'
          + '<a href="' + escapeHtml(giftUrl) + '" class="name">' + titleEsc + '</a>'
          + '<p class="lv-gift-badge">Free gift &middot; Yours at $' + threshold + '+</p>'
          + '<p class="price" data-lv-line-price><s class="lv-gift-strike">' + money(compareCents) + '</s> <strong class="lv-gift-free">FREE</strong></p>'
        + '</div>'
      + '</article>';
    }
    var unitCents = it.final_line_price && it.quantity ? (it.final_line_price / it.quantity) : (it.final_price || 0);
    var dis = pendingKeys[it.key] ? ' disabled' : '';
    return '<article class="lv-cart-item" data-lv-item data-handle="' + escapeHtml(it.handle || '') + '" data-key="' + escapeHtml(it.key) + '" data-unit-cents="' + unitCents + '" data-line-price="' + (it.final_line_price || 0) + '">'
      + '<a href="' + escapeHtml(it.url) + '" class="thumb" aria-label="View ' + titleEsc + '">' + thumbHtml + '</a>'
      + '<div class="info">'
        + '<a href="' + escapeHtml(it.url) + '" class="name">' + titleEsc + '</a>'
        + variantHtml
        + '<p class="price" data-lv-line-price>' + money(it.final_line_price) + '</p>'
        + '<div class="controls">'
          + '<div class="qty" role="group" aria-label="Quantity for ' + titleEsc + '">'
            + '<button type="button" data-lv-action="qty-dec" aria-label="Decrease quantity of ' + titleEsc + '"' + dis + '>-</button>'
            + '<span class="count" data-lv-qty>' + it.quantity + '</span>'
            + '<button type="button" data-lv-action="qty-inc" aria-label="Increase quantity of ' + titleEsc + '"' + dis + '>+</button>'
          + '</div>'
          + '<button type="button" class="remove" data-lv-action="remove" aria-label="Remove ' + titleEsc + ' from bag"' + dis + '>Remove</button>'
        + '</div>'
      + '</div>'
      + '</article>';
  }

  function renderShipAndTotals(totalPriceCents, qualifyingCents) {
    var subtotalEl = overlay.querySelector('[data-lv-subtotal]');
    var totalEl = overlay.querySelector('[data-lv-total]');
    var shippingEl = overlay.querySelector('[data-lv-shipping]');
    var checkoutAmount = overlay.querySelector('[data-lv-checkout-amount]');
    if (subtotalEl) subtotalEl.textContent = money(totalPriceCents);
    if (totalEl) totalEl.textContent = money(totalPriceCents);
    if (checkoutAmount) checkoutAmount.textContent = moneyShort(totalPriceCents);

    // qualifyingCents is passed by renderCart from qualifyingSubtotal(cart).
    // Optimistic paths (qty +/-, remove) fall through to totalPriceCents; the
    // next server round-trip re-renders with the real qualifying value.
    var q = (typeof qualifyingCents === 'number') ? qualifyingCents : totalPriceCents;

    var label = overlay.querySelector('[data-lv-ship-label]');
    var fill = overlay.querySelector('[data-lv-ship-fill]');
    var unlocked = q >= thresholdCents;
    // 2026-09-23 (Derrick): the bar is about the free hat only. US shipping is a flat $0 rate
    // for every order, so the threshold never unlocked shipping; the drawer claims neither.
    if (shippingEl) shippingEl.textContent = 'Calculated at checkout';
    if (unlocked) {
      if (label) label.innerHTML = '<strong style="color:#2e6b3c">FREE hat unlocked</strong><span>' + moneyShort(q) + ' / $' + threshold + '</span>';
      if (fill) fill.style.width = '100%';
    } else {
      var remain = thresholdCents - q;
      if (label) label.innerHTML = '<span>' + moneyShort(remain) + ' more for a <strong>FREE hat</strong></span><span>' + moneyShort(q) + ' / $' + threshold + '</span>';
      if (fill) fill.style.width = Math.round((q / thresholdCents) * 100) + '%';
    }

    // Upsell zone: visible until threshold is reached (and while it has cards to show —
    // see the anchor logic below).
    upsellUnlocked = unlocked;
    applyUpsellVisibility();
  }

  /* ===========================================================
   * Upsell zone — generated once per cart ANCHOR, shrink-only.
   * Operator ruling 2026-07-08: recommendations are generated for the first cart item
   * and only SHRINK as the buyer adds; never rotate fresh ones in to refill the slot;
   * regenerate only when the anchor changes; hide the block when nothing is left.
   *
   *   - The anchor is remembered per cart in localStorage (`lv_upsell_anchor:<cart.token>`):
   *     set on first observation (page load with a non-empty cart adopts the server-rendered
   *     anchor; an add into an empty cart adopts that item), cleared only when the cart is
   *     observed empty. If the anchor line is removed while other lines remain, the zone is
   *     HIDDEN, not regenerated for the next item.
   *   - The only network call is `/?sections=lv-cart-drawer` (Section Rendering API), and it
   *     runs ONLY when the zone's rendered anchor differs from the remembered one. A
   *     monotonic generation drops late responses; a response whose anchor is not the
   *     remembered one is dropped too; every applied response is re-shrunk immediately.
   *   - The zone starts `data-lv-upsell-pending` (visibility hidden) until the first
   *     reconciliation, so a stale server render never flashes.
   * =========================================================== */
  var upsellZone = overlay.querySelector('[data-lv-upsell-zone]');
  var upsellWrap = overlay.querySelector('[data-lv-upsells]');
  var upsellUnlocked = false;
  var upsellExhausted = false;
  var upsellGen = 0;

  /* One fixed key. The earlier version keyed on `cart.token`, which is only reachable through
   * /cart.js -- and the whole point of the 2026-09-10 rework is that a normal add never fetches
   * it. The anchor is cleared whenever the cart is observed empty, which is the reset condition
   * the ruling specifies, so a per-cart key bought nothing. */
  var UPSELL_ANCHOR_KEY = 'lv_upsell_anchor';
  function getStoredAnchor() {
    try { return window.localStorage.getItem(UPSELL_ANCHOR_KEY) || ''; } catch (e) { return ''; }
  }
  function setStoredAnchor(handle) {
    try {
      if (handle) window.localStorage.setItem(UPSELL_ANCHOR_KEY, handle);
      else window.localStorage.removeItem(UPSELL_ANCHOR_KEY);
    } catch (e) { /* private mode etc.: fall back to the server-rendered anchor */ }
  }
  function cartHandleSet(handles) {
    var s = {};
    (handles || []).forEach(function (h) { if (h) s[h] = true; });
    return s;
  }
  // Handles of the lines currently PAINTED in the drawer. Works for both the Liquid first
  // paint and the JS re-render, because both stamp data-handle on every line.
  function domCartHandles() {
    return [].slice.call(overlay.querySelectorAll('[data-lv-item]'))
      .map(function (e) { return e.getAttribute('data-handle') || ''; })
      .filter(Boolean);
  }
  function applyUpsellVisibility() {
    if (!upsellZone) return;
    upsellZone.style.display = (upsellUnlocked || upsellExhausted) ? 'none' : '';
  }
  // Hide every card whose product is now in the cart. Returns the count still visible.
  function shrinkUpsells(handles) {
    if (!upsellWrap) return 0;
    var inCart = cartHandleSet(handles);
    var visible = 0;
    upsellWrap.querySelectorAll('[data-lv-upsell]').forEach(function (card) {
      var h = card.getAttribute('data-handle');
      var hide = !!(h && inCart[h]);
      if (hide) card.setAttribute('hidden', ''); else card.removeAttribute('hidden');
      if (!hide && card.style.display !== 'none') visible++;
    });
    return visible;
  }
  function fetchUpsells(anchor) {
    var gen = ++upsellGen;
    fetch('/?sections=' + SECTION_ID, { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (gen !== upsellGen) return;                       // a newer request superseded this one
        var html = j && j[SECTION_ID];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-lv-upsells]');
        if (!fresh) return;
        var freshAnchor = fresh.getAttribute('data-lv-upsell-anchor') || '';
        if (freshAnchor !== anchor) return;                  // server saw a different cart; keep state
        upsellWrap.innerHTML = fresh.innerHTML;
        upsellWrap.setAttribute('data-lv-upsell-anchor', freshAnchor);
        upsellExhausted = shrinkUpsells(domCartHandles()) === 0;  // re-shrink against what is painted
        applyUpsellVisibility();
      })
      .catch(function () { /* keep the shrunk state; retried on the next anchor set */ });
  }

  /* Anchor reconciliation, driven entirely by what is PAINTED plus the server's own anchor
   * attribute. Needs no cart JSON, so it behaves identically after a sections-based add and
   * after a /cart.js render. Ruling 2026-07-08: generate once per anchor, shrink only, never
   * rotate to the next item; reset only when the cart empties. */
  function reconcileUpsellsCore(handles) {
    if (!upsellZone || !upsellWrap) return;
    var domAnchor = upsellWrap.getAttribute('data-lv-upsell-anchor') || '';
    var stored = getStoredAnchor();
    if (!handles.length) {
      // Empty cart: forget the anchor. Generic cards only exist on a fresh empty-cart render;
      // after an anchor-specific swap there is nothing sensible to show.
      setStoredAnchor('');
      upsellExhausted = domAnchor !== '' || shrinkUpsells(handles) === 0;
    } else if (!stored) {
      // First observation: adopt the anchor the SERVER rendered this zone for. It is
      // authoritative and assumes nothing about cart ordering.
      stored = domAnchor || handles[handles.length - 1];
      setStoredAnchor(stored);
      if (domAnchor !== stored) { upsellExhausted = false; fetchUpsells(stored); }
      else upsellExhausted = shrinkUpsells(handles) === 0;
    } else if (!cartHandleSet(handles)[stored]) {
      // The anchor line left a non-empty cart: hide, never rotate to the next item.
      upsellExhausted = true;
    } else if (domAnchor !== stored) {
      upsellExhausted = false;
      fetchUpsells(stored);
    } else {
      upsellExhausted = shrinkUpsells(handles) === 0;
    }
    upsellZone.removeAttribute('data-lv-upsell-pending');
    applyUpsellVisibility();
  }
  function reconcileUpsells(cart) {
    if (!cart) return;
    reconcileUpsellsCore((cart.items || []).map(function (i) { return i.handle || ''; }).filter(Boolean));
  }
  function reconcileUpsellsFromDom() {
    reconcileUpsellsCore(domCartHandles());
  }

  /* Free-gift auto-sync. Called after every /cart.js render.
     Adds the gift line when qualifying subtotal crosses threshold, removes when
     it drops back below. Guarded so a concurrent refresh() can't fire a second
     add/remove mid-flight. No-op if no gift variant configured.

     Batching in the qty-dec/remove handlers means the REMOVE side of this
     function is a fallback for edge cases (page load with a stale gift, or
     mutations that didn't go through our handlers). The ADD side stays here
     as the primary path — skeleton first, then the network. */
  function renderGiftSkeleton() {
    var items = overlay.querySelector('[data-lv-cart-items]');
    if (!items || items.querySelector('.lv-cart-item-gift')) return;
    var node = document.createElement('article');
    node.className = 'lv-cart-item lv-cart-item-gift lv-cart-item-pending';
    node.setAttribute('data-lv-pending-gift', '1');
    node.innerHTML =
        '<div class="thumb"></div>'
      + '<div class="info">'
        + '<p class="name">Free gift</p>'
        + '<p class="lv-gift-badge">Free gift &middot; Yours at $' + threshold + '+</p>'
        + '<p class="price">Adding your free gift…</p>'
      + '</div>';
    items.appendChild(node);
  }

  function syncFreeGift(cart) {
    if (!giftVariantId || giftAdjustInFlight || !cart) return;
    var q = qualifyingSubtotal(cart);
    var giftLine = findGiftLine(cart);

    // RETRACT. We auto-add the REAL cap and rely on a Shopify automatic discount to zero
    // it server-side. If the server did NOT discount it, the shopper is holding a $120
    // item they never asked for -- so take it straight back out and stop trying. This is
    // the safety net for the discount being off, expired, mis-scoped, or out-competed by
    // another automatic discount (only one applies per cart unless combinesWith allows it).
    if (giftLine && giftLine.final_line_price > 0) {
      giftAdjustInFlight = true;
      giftAddFailed = true;   // do not re-add this session; it would loop
      beginMutation();
      changeLine(giftLine.key, 0)
        .then(function (fresh) { renderCart(fresh); })
        .catch(function (err) { console.warn('free-gift retract error', err); })
        .finally(function () { giftAdjustInFlight = false; endMutation(); });
      return;
    }

    // Never auto-add a second cap on top of one the shopper chose to buy.
    if (q >= thresholdCents && !giftLine && !giftAddFailed && !hasGiftVariant(cart)) {
      giftAdjustInFlight = true;
      beginMutation();
      renderGiftSkeleton();
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: giftVariantId, quantity: 1, properties: { _free_gift: 'true' } }] })
      })
        .then(function (r) { return r.json(); })
        .then(function (resp) {
          if (resp && resp.status && resp.status >= 400) {
            giftAddFailed = true;
            console.warn('free-gift add failed', resp);
            var stale = overlay.querySelector('[data-lv-pending-gift]');
            if (stale) stale.remove();
            return null;
          }
          return fetchCart();
        })
        .then(function (fresh) { if (fresh) renderCart(fresh); })
        .catch(function (err) {
          console.warn('free-gift add error', err);
          giftAddFailed = true;
          var stale2 = overlay.querySelector('[data-lv-pending-gift]');
          if (stale2) stale2.remove();
        })
        .finally(function () { giftAdjustInFlight = false; endMutation(); });
      return;
    }

    if (q < thresholdCents && giftLine) {
      giftAdjustInFlight = true;
      beginMutation();
      // Optimistically fade the gift line so the customer sees it leaving
      // instantly, before the network confirms.
      var giftDom = overlay.querySelector('[data-lv-gift="1"]');
      if (giftDom) { giftDom.style.transition = 'opacity 120ms ease'; giftDom.style.opacity = '0.15'; giftDom.style.pointerEvents = 'none'; }
      changeLine(giftLine.key, 0)
        .then(function (fresh) { renderCart(fresh); })
        .catch(function (err) { console.warn('free-gift remove error', err); })
        .finally(function () { giftAdjustInFlight = false; endMutation(); });
      return;
    }
  }

  function renderCart(cart) {
    if (!cart) return;
    cartCache = cart;
    var items = overlay.querySelector('[data-lv-cart-items]');
    if (items) {
      if (!cart.items || cart.items.length === 0) {
        items.innerHTML = '<p class="lv-cart-empty">Your bag is empty. Pick a piece from the drop and we will hold it.</p>';
      } else {
        var html = '';
        for (var i = 0; i < cart.items.length; i++) html += renderItem(cart.items[i]);
        items.innerHTML = html;
      }
    }
    renderShipAndTotals(cart.total_price, qualifyingSubtotal(cart));
    updateBagCount(cart.item_count);
    reconcileUpsells(cart);
    syncFreeGift(cart);
    // 2026-09-23: /cart (main-cart.liquid) listens so the page behind the drawer never shows a
    // cart the drawer has since changed (e.g. the free hat retracted after a Remove).
    try { document.dispatchEvent(new CustomEvent('lv:cart-rendered', { detail: cart })); } catch (e) {}
  }



  // Read product snapshot from the PDP DOM for optimistic UI
  function pdpSnapshot(form) {
    var title = (document.querySelector('.lv-title') || {}).textContent || 'Adding to bag';
    var img = (document.getElementById('lv-main-img') || {}).src || '';
    var priceText = (document.querySelector('.lv-price') || {}).textContent || '';
    var priceCents = 0;
    var m = priceText.replace(/[, ]/g, '').match(/\$([\d.]+)/);
    if (m) priceCents = Math.round(parseFloat(m[1]) * 100);
    var variant = '';
    var sel = form ? form.querySelectorAll('.selected') : [];
    if (sel.length) {
      var parts = [];
      sel.forEach(function (s) { parts.push((s.textContent || '').trim()); });
      variant = parts.filter(Boolean).join(' · ');
    }
    return { title: title.trim(), img: img, priceCents: priceCents, variant: variant };
  }
  function upsellSnapshot(card) {
    var img = (card.querySelector('img') || {}).src || '';
    var title = (card.querySelector('.name') || {}).textContent || 'Adding to bag';
    var priceText = (card.querySelector('.price') || {}).textContent || '';
    var priceCents = 0;
    var m = priceText.replace(/[, ]/g, '').match(/\$([\d.]+)/);
    if (m) priceCents = Math.round(parseFloat(m[1]) * 100);
    return { title: title.trim(), img: img, priceCents: priceCents, variant: '' };
  }

  function refresh() {
    return fetchCart()
      .then(renderCart)
      .catch(function (err) { console.warn('cart refresh failed', err); });
  }

  // Openers / closers
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-lv-cart-open]');
    if (opener) {
      // Let middle/cmd/ctrl/shift-clicks fall through so "open in new tab"
      // still works if the opener is a link. The current header opener is a
      // <button> so this is forward-compatible.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openDrawer(opener);                            // open instantly + capture opener
      if (cartCache) renderCart(cartCache);          // show last-known immediately
      refresh();                                     // sync in background
      return;
    }
    var closer = e.target.closest('[data-lv-cart-close]');
    if (closer) { e.preventDefault(); closeDrawer(); return; }
  });

  // Drawer-internal actions
  overlay.addEventListener('click', function (e) {
    var t = e.target.closest('[data-lv-action]');
    if (!t) return;
    var action = t.getAttribute('data-lv-action');
    var item = t.closest('[data-lv-item]');
    var key = item ? item.getAttribute('data-key') : null;

    if ((action === 'qty-inc' || action === 'qty-dec') && item) {
      e.preventDefault();
      // Race guard: skip if this line already has a /cart/change.js in flight.
      // The debounce alone coalesces fast clicks INTO a single server call but doesn't
      // prevent a second user click during the round-trip from snapshotting unit_cents=0
      // off a half-rendered DOM. The pending guard plus the disabled-button render
      // (in renderItem) closes that window.
      if (pendingKeys[key]) return;
      var q = item.querySelector('[data-lv-qty]');
      var cur = parseInt(q.textContent, 10);
      var next = action === 'qty-inc' ? cur + 1 : Math.max(0, cur - 1);
      // Optimistic update: bump qty + line price + totals immediately
      // Fall back to data-line-price / quantity when data-unit-cents is missing
      // (first-paint Liquid render now emits data-unit-cents too — see lv-cart-item.liquid).
      var unit = parseInt(item.getAttribute('data-unit-cents'), 10);
      if (!unit) {
        var linePrice = parseInt(item.getAttribute('data-line-price'), 10) || 0;
        unit = cur > 0 ? Math.round(linePrice / cur) : 0;
      }
      var delta = (next - cur) * unit;
      q.textContent = next;
      var lp = item.querySelector('[data-lv-line-price]');
      if (lp) lp.textContent = money(unit * next);
      if (cartCache) {
        cartCache.total_price = (cartCache.total_price || 0) + delta;
        cartCache.item_count = Math.max(0, (cartCache.item_count || 0) + (next - cur));
        renderShipAndTotals(cartCache.total_price);
        updateBagCount(cartCache.item_count);
      }
      // Debounce server sync per line so rapid clicks coalesce
      clearTimeout(qtySyncTimers[key]);
      qtySyncTimers[key] = setTimeout(function () {
        delete qtySyncTimers[key];
        pendingKeys[key] = true;
        beginMutation();
        // Race-safe gift sync: if this qty change will push qualifying subtotal
        // below the threshold AND a gift line is in cart, batch both changes
        // into ONE /cart/update.js request. That way the gift line is removed
        // server-side in the same round-trip — no window for Checkout to sneak
        // through with a stale cart.
        var predicted = predictedQualifying(cartCache, (function () { var p = {}; p[key] = next; return p; })());
        var giftLine = giftVariantId ? findGiftLine(cartCache) : null;
        var willDrop = giftLine && predicted < thresholdCents;
        if (willDrop) {
          var giftItem = overlay.querySelector('[data-lv-gift="1"]');
          if (giftItem) {
            giftItem.style.transition = 'opacity 120ms ease';
            giftItem.style.opacity = '0.15';
            giftItem.style.pointerEvents = 'none';
          }
          giftAdjustInFlight = true;
          var updates = {};
          updates[key] = next;
          updates[giftLine.key] = 0;
          updateCartLines(updates)
            .then(function (cart) {
              delete pendingKeys[key];
              renderCart(cart);
            })
            .catch(function (err) {
              delete pendingKeys[key];
              console.warn('batch qty+gift sync failed', err);
              refresh();
            })
            .finally(function () { giftAdjustInFlight = false; endMutation(); });
        } else {
          changeLine(key, next)
            .then(function (cart) { delete pendingKeys[key]; renderCart(cart); })
            .catch(function (err) {
              delete pendingKeys[key];
              console.warn('cart qty sync failed', err);
              refresh();
            })
            .finally(function () { endMutation(); });
        }
      }, 280);
      return;
    }
    if (action === 'remove' && item) {
      e.preventDefault();
      if (pendingKeys[key]) return;
      pendingKeys[key] = true;
      // Optimistic remove: yank from DOM + totals immediately
      var qNow = parseInt(item.querySelector('[data-lv-qty]').textContent, 10) || 0;
      var unitNow = parseInt(item.getAttribute('data-unit-cents'), 10);
      if (!unitNow) {
        var linePriceR = parseInt(item.getAttribute('data-line-price'), 10) || 0;
        unitNow = qNow > 0 ? Math.round(linePriceR / qNow) : 0;
      }
      var prevCache = cartCache ? { total_price: cartCache.total_price, item_count: cartCache.item_count } : null;
      if (cartCache) {
        cartCache.total_price = Math.max(0, (cartCache.total_price || 0) - (qNow * unitNow));
        cartCache.item_count = Math.max(0, (cartCache.item_count || 0) - qNow);
        renderShipAndTotals(cartCache.total_price);
        updateBagCount(cartCache.item_count);
      }
      item.remove();
      beginMutation();
      // If removing this item drops qualifying below threshold and there's a
      // gift line, batch both removals into ONE /cart/update.js call so the
      // gift is gone from the server in the same round-trip.
      var predictedR = predictedQualifying(cartCache, (function () { var p = {}; p[key] = 0; return p; })());
      var giftLineR = giftVariantId ? findGiftLine(cartCache) : null;
      var willDropR = giftLineR && predictedR < thresholdCents;
      if (willDropR) {
        var giftItemR = overlay.querySelector('[data-lv-gift="1"]');
        if (giftItemR) {
          giftItemR.style.transition = 'opacity 120ms ease';
          giftItemR.style.opacity = '0.15';
          giftItemR.style.pointerEvents = 'none';
        }
        giftAdjustInFlight = true;
        var updatesR = {};
        updatesR[key] = 0;
        updatesR[giftLineR.key] = 0;
        updateCartLines(updatesR).then(function (cart) {
          delete pendingKeys[key];
          showToast('Removed from bag.');
          renderCart(cart);
        }).catch(function () {
          delete pendingKeys[key];
          showToast('Could not remove — refreshing bag.');
          if (cartCache && prevCache) {
            cartCache.total_price = prevCache.total_price;
            cartCache.item_count = prevCache.item_count;
          }
          refresh();
        }).finally(function () { giftAdjustInFlight = false; endMutation(); });
      } else {
        changeLine(key, 0).then(function (cart) {
          delete pendingKeys[key];
          showToast('Removed from bag.');
          renderCart(cart);
        }).catch(function () {
          delete pendingKeys[key];
          showToast('Could not remove — refreshing bag.');
          if (cartCache && prevCache) {
            cartCache.total_price = prevCache.total_price;
            cartCache.item_count = prevCache.item_count;
          }
          refresh();
        }).finally(function () { endMutation(); });
      }
      return;
    }
    if (action === 'add-upsell') {
      e.preventDefault();
      var card = t.closest('[data-lv-upsell]');
      var variantId = parseInt(card.getAttribute('data-variant-id'), 10);
      if (!variantId) { showToast('Sorry, this piece is out of stock.'); return; }
      var addBtn = card.querySelector('.add');
      var addText = addBtn ? addBtn.textContent : '';
      if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Adding...'; }
      // ONE request. The re-rendered section already hides this card (it excludes anything in
      // the cart), so there is no optimistic row and no card-hiding animation to roll back.
      addToCart(variantId, 1).then(function (resp) {
        if (resp && resp.status === 422) {
          showToast('Sorry, this piece is out of stock.');
          if (addBtn) { addBtn.disabled = false; addBtn.textContent = addText; }
          return;
        }
        var summary = applyDrawerSection(resp && resp.sections && resp.sections[SECTION_ID]);
        if (summary) settleGift(summary);
        else refresh();
      }).catch(function () {
        showToast('Something went wrong. Try again.');
        if (addBtn) { addBtn.disabled = false; addBtn.textContent = addText; }
      });
      return;
    }
  });

  // PDP form submit -- intercept Add to Bag. ONE request: /cart/add.js returns the rendered
  // drawer, so there is nothing to optimistically fake and no "adding" animation to run.
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    // Double-submit guard: a disabled button alone doesn't block Enter-key submit from a
    // focused input.
    if (pdpAddInFlight) return;
    pdpAddInFlight = true;
    var atc = form.querySelector('[data-lv-atc]');
    var origLabel = atc ? atc.querySelector('[data-lv-atc-label]') : null;
    var origText = origLabel ? origLabel.textContent : null;
    if (atc) { atc.disabled = true; if (origLabel) origLabel.textContent = 'Adding...'; }
    function restore() {
      if (atc) { atc.disabled = false; if (origLabel) origLabel.textContent = origText; }
      pdpAddInFlight = false;
    }
    openDrawer();
    var data = new FormData(form);
    data.append('sections', SECTION_ID);
    data.append('sections_url', sectionsUrl());
    fetch('/cart/add.js', { method: 'POST', body: data, headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (resp) {
        if (resp && resp.status === 422) {
          showToast(resp.description || 'Out of stock');
          restore();
          return;
        }
        var summary = applyDrawerSection(resp && resp.sections && resp.sections[SECTION_ID]);
        restore();
        if (summary) settleGift(summary);
        else refresh();          // section missing (older API/theme): fall back to a cart read
      })
      .catch(function () {
        showToast('Something went wrong. Try again.');
        restore();
      });
  });

  // PDP "Layer It" one-click upsell ATC -- a button outside any form. ONE request.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-lv-pdp-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) { showToast('Sorry, this piece is out of stock.'); return; }
    btn.disabled = true;
    var origText = btn.textContent;
    btn.textContent = 'Adding...';
    openDrawer();
    addToCart(variantId, 1).then(function (resp) {
      if (resp && resp.status === 422) {
        showToast(resp.description || 'Out of stock');
        btn.disabled = false; btn.textContent = origText;
        return;
      }
      var summary = applyDrawerSection(resp && resp.sections && resp.sections[SECTION_ID]);
      if (summary) settleGift(summary); else refresh();
      btn.textContent = 'Added';
      setTimeout(function () { btn.disabled = false; btn.textContent = origText; }, 1400);
    }).catch(function () {
      showToast('Something went wrong. Try again.');
      btn.disabled = false; btn.textContent = origText;
    });
  });

  // PDP option pickers (size / color) update hidden id input.
  // Buttons are role="radio" so the correct ARIA attribute is aria-checked,
  // not aria-pressed (which is for toggle buttons). lv-pdp.js's rePressGroup
  // does the same thing.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-lv-size], [data-lv-color]');
    if (!btn) return;
    var grid = btn.parentElement;
    grid.querySelectorAll('button').forEach(function (b) { b.setAttribute('aria-checked', 'false'); });
    btn.setAttribute('aria-checked', 'true');
    var label = btn.getAttribute('data-lv-size') || btn.getAttribute('data-lv-color');
    var selectedSlot = grid.parentElement.querySelector('.selected');
    if (selectedSlot) selectedSlot.textContent = label;
  });

  // PDP gallery thumb -> swap hero. Thumbs are role="tab" so use aria-selected.
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-lv-thumb]');
    if (!thumb) return;
    var src = thumb.getAttribute('data-src');
    var main = document.querySelector('#lv-main-img');
    if (main && src) main.src = src;
    thumb.parentElement.querySelectorAll('button').forEach(function (b) { b.setAttribute('aria-selected', 'false'); });
    thumb.setAttribute('aria-selected', 'true');
  });

  // 2026-09-10: the size-guide branches came out of this handler when that modal became a
  // PDP accordion. Only the CART's own Escape dismissal lives here now -- do not delete the
  // whole block on the assumption it is all size-guide code.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (!overlay.classList.contains('open')) return;
    closeDrawer();
  });

  /* ===========================================================
   * Upsell variant picker modal
   * Multi-variant upsell products open this picker (color + size)
   * instead of force-adding the first available variant.
   * =========================================================== */
  var pickerModal = document.querySelector('[data-lv-upsell-picker-overlay]');
  var pickerState = null;       // { product, colorIdx, sizeIdx, selection: {Color, Size}, sourceCard }

  function pickerBody() { return pickerModal ? pickerModal.querySelector('[data-lv-upsell-picker-body]') : null; }

  function openPicker(handle, sourceCard) {
    if (!pickerModal) return;
    var body = pickerBody();
    if (body) body.innerHTML = '<p class="lv-picker-loading">Loading…</p>';
    pickerModal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (product) { initPickerFor(product, sourceCard); })
      .catch(function () {
        if (body) body.innerHTML = '<p class="lv-picker-error">Couldn\'t load this product. Please try again.</p>';
      });
  }

  function closePicker() {
    if (!pickerModal) return;
    pickerModal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    pickerState = null;
  }

  function uniqueOptionValues(product, optIndex) {
    var key = 'option' + (optIndex + 1);
    var seen = {}; var out = [];
    product.variants.forEach(function (v) {
      var val = v[key];
      if (val == null || seen[val]) return;
      seen[val] = true;
      out.push(val);
    });
    return out;
  }

  function findPickerVariant() {
    if (!pickerState) return null;
    var s = pickerState;
    return s.product.variants.filter(function (v) {
      if (s.colorIdx >= 0 && v['option' + (s.colorIdx + 1)] !== s.selection.Color) return false;
      if (s.sizeIdx  >= 0 && v['option' + (s.sizeIdx  + 1)] !== s.selection.Size)  return false;
      return true;
    })[0];
  }

  function initPickerFor(product, sourceCard) {
    if (!product || !product.variants || !product.variants.length) {
      var b = pickerBody();
      if (b) b.innerHTML = '<p class="lv-picker-error">No variants available.</p>';
      return;
    }
    // /products/{handle}.js returns `options` as a normalized array of strings (option names).
    // `options_with_values` is only available from Liquid; do not look for it here.
    var optionNames = (product.options || []).map(function (o) {
      return (typeof o === 'string') ? o : (o && o.name) || '';
    });
    var colorIdx = -1, sizeIdx = -1;
    optionNames.forEach(function (n, i) {
      if (colorIdx < 0 && (n === 'Color' || n === 'Colour')) colorIdx = i;
    });
    optionNames.forEach(function (n, i) {
      if (i === colorIdx) return;
      if (sizeIdx < 0) sizeIdx = i;
    });
    var firstAvail = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
    pickerState = {
      product: product,
      optionNames: optionNames,
      colorIdx: colorIdx,
      sizeIdx: sizeIdx,
      selection: {
        Color: colorIdx >= 0 ? firstAvail['option' + (colorIdx + 1)] : null,
        Size:  sizeIdx  >= 0 ? firstAvail['option' + (sizeIdx  + 1)] : null
      },
      sourceCard: sourceCard || null
    };
    renderPicker();
  }

  function renderPicker() {
    var body = pickerBody();
    if (!body || !pickerState) return;
    var p = pickerState.product;
    var v = findPickerVariant() || p.variants[0];
    var heroSrc = (v.featured_image && v.featured_image.src) ? v.featured_image.src
                 : (p.featured_image || (p.images && p.images[0]) || '');
    var html = '';
    html += '<div class="lv-picker">';
    html +=   '<div class="lv-picker-hero">';
    if (heroSrc) {
      html += '<img data-lv-picker-img src="' + escapeHtml(heroSrc) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">';
    }
    html +=   '</div>';
    html +=   '<div class="lv-picker-info">';
    html +=     '<p class="lv-picker-eyebrow">Pair It</p>';
    html +=     '<h4 class="lv-picker-name">' + escapeHtml(p.title) + '</h4>';
    html +=     '<p class="lv-picker-price" data-lv-picker-price>' + moneyShort(v.price) + '</p>';
    // Render option pickers — iterate the normalized optionNames stored on pickerState.
    // Skip any option with a single value (e.g. single-color products) — no real choice to make,
    // and the value is auto-selected via initPickerFor's selection seed.
    (pickerState.optionNames || []).forEach(function (name, i) {
      var values = uniqueOptionValues(p, i);
      if (values.length <= 1) return;
      var isColor = (i === pickerState.colorIdx);
      var groupClass = isColor ? 'lv-color-grid' : 'lv-size-grid';
      var btnClass = isColor ? 'lv-color-btn' : 'lv-size-btn';
      var dataKey = isColor ? 'data-lv-picker-color' : 'data-lv-picker-size';
      var labelKey = isColor ? 'data-lv-picker-selected-color' : 'data-lv-picker-selected-size';
      var current = isColor ? pickerState.selection.Color : pickerState.selection.Size;
      html += '<div class="lv-' + (isColor ? 'color' : 'size') + '-block">';
      html +=   '<div class="lv-label-row"><span>' + escapeHtml(name) + ': <span class="selected" ' + labelKey + '>' + escapeHtml(current || '') + '</span></span></div>';
      html +=   '<div class="' + groupClass + '" role="radiogroup" aria-label="' + escapeHtml(name) + '">';
      values.forEach(function (val) {
        var available = p.variants.some(function (vv) {
          if (vv['option' + (i + 1)] !== val) return false;
          if (isColor && pickerState.sizeIdx >= 0 && pickerState.selection.Size && vv['option' + (pickerState.sizeIdx + 1)] !== pickerState.selection.Size) return false;
          if (!isColor && pickerState.colorIdx >= 0 && pickerState.selection.Color && vv['option' + (pickerState.colorIdx + 1)] !== pickerState.selection.Color) return false;
          return vv.available;
        });
        var classes = btnClass + (available ? '' : ' lv-opt-soldout');
        var checked = (val === current) ? 'true' : 'false';
        var soldOutSuffix = available ? '' : ' (sold out)';
        html += '<button type="button" role="radio" class="' + classes + '" ' + dataKey + '="' + escapeHtml(val) + '" aria-checked="' + checked + '" aria-label="' + escapeHtml(name) + ' ' + escapeHtml(val) + soldOutSuffix + '">' + escapeHtml(val) + '</button>';
      });
      html +=   '</div>';
      html += '</div>';
    });
    var canBuy = v && v.available;
    html +=     '<button type="button" class="lv-atc lv-picker-add" data-lv-picker-add' + (canBuy ? '' : ' disabled') + '>'
              + (canBuy ? ('Add to Bag · ' + moneyShort(v.price)) : 'Sold Out')
              + '</button>';
    html +=   '</div>';
    html += '</div>';
    body.innerHTML = html;
  }

  function refreshPickerForCurrentVariant() {
    var v = findPickerVariant();
    var body = pickerBody();
    if (!body) return;
    var img = body.querySelector('[data-lv-picker-img]');
    var price = body.querySelector('[data-lv-picker-price]');
    var add = body.querySelector('[data-lv-picker-add]');
    if (v) {
      if (v.featured_image && v.featured_image.src && img) img.src = v.featured_image.src;
      if (price) price.textContent = moneyShort(v.price);
      if (add) {
        if (v.available) {
          add.removeAttribute('disabled');
          add.textContent = 'Add to Bag · ' + moneyShort(v.price);
        } else {
          add.setAttribute('disabled', '');
          add.textContent = 'Sold Out';
        }
      }
    } else {
      if (add) { add.setAttribute('disabled', ''); add.textContent = 'Unavailable'; }
    }
    // Re-stripe option buttons for current selection
    var p = pickerState.product;
    (pickerState.optionNames || []).forEach(function (name, i) {
      var isColor = (i === pickerState.colorIdx);
      var dataKey = isColor ? 'data-lv-picker-color' : 'data-lv-picker-size';
      body.querySelectorAll('[' + dataKey + ']').forEach(function (btn) {
        var val = btn.getAttribute(dataKey);
        var avail = p.variants.some(function (vv) {
          if (vv['option' + (i + 1)] !== val) return false;
          if (isColor && pickerState.sizeIdx >= 0 && pickerState.selection.Size && vv['option' + (pickerState.sizeIdx + 1)] !== pickerState.selection.Size) return false;
          if (!isColor && pickerState.colorIdx >= 0 && pickerState.selection.Color && vv['option' + (pickerState.colorIdx + 1)] !== pickerState.selection.Color) return false;
          return vv.available;
        });
        btn.classList.toggle('lv-opt-soldout', !avail);
      });
    });
  }

  // Open the picker from any [data-lv-action="open-upsell-picker"] click — works from cart drawer cards AND PDP "Layer It" card.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-lv-action="open-upsell-picker"]');
    if (!trigger) return;
    e.preventDefault();
    var handle = trigger.getAttribute('data-handle');
    if (!handle) return;
    // Source card (for hide-on-success) — the upsell card in the cart drawer or the PDP layer-it card.
    var sourceCard = trigger.closest('[data-lv-upsell]') || trigger.closest('.lv-pdp-upsell');
    openPicker(handle, sourceCard);
  });

  // Picker close (X, backdrop)
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-lv-upsell-picker-close]')) {
      e.preventDefault();
      closePicker();
    }
  });

  // Escape closes the picker first if open (before falling through to the cart)
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (pickerModal && !pickerModal.hasAttribute('hidden')) {
      closePicker();
      e.stopPropagation();
    }
  }, true);

  // Picker option clicks + Add to Bag (delegated; modal is outside the cart overlay so we use document)
  document.addEventListener('click', function (e) {
    if (!pickerModal || pickerModal.hasAttribute('hidden') || !pickerState) return;
    var colorBtn = e.target.closest('[data-lv-picker-color]');
    if (colorBtn) {
      e.preventDefault();
      pickerState.selection.Color = colorBtn.getAttribute('data-lv-picker-color');
      pickerBody().querySelectorAll('[data-lv-picker-color]').forEach(function (b) {
        b.setAttribute('aria-checked', b === colorBtn ? 'true' : 'false');
      });
      var label = pickerBody().querySelector('[data-lv-picker-selected-color]');
      if (label) label.textContent = pickerState.selection.Color;
      refreshPickerForCurrentVariant();
      return;
    }
    var sizeBtn = e.target.closest('[data-lv-picker-size]');
    if (sizeBtn) {
      e.preventDefault();
      pickerState.selection.Size = sizeBtn.getAttribute('data-lv-picker-size');
      pickerBody().querySelectorAll('[data-lv-picker-size]').forEach(function (b) {
        b.setAttribute('aria-checked', b === sizeBtn ? 'true' : 'false');
      });
      var sizeLabel = pickerBody().querySelector('[data-lv-picker-selected-size]');
      if (sizeLabel) sizeLabel.textContent = pickerState.selection.Size;
      refreshPickerForCurrentVariant();
      return;
    }
    var addBtn = e.target.closest('[data-lv-picker-add]');
    if (addBtn && !addBtn.hasAttribute('disabled')) {
      e.preventDefault();
      var v = findPickerVariant();
      if (!v || !v.available) { showToast('That combination is sold out.'); return; }
      var sourceCard = pickerState.sourceCard;
      var origLabel = addBtn.textContent;
      addBtn.setAttribute('disabled', '');
      addBtn.textContent = 'Adding…';
      addToCart(v.id, 1).then(function (resp) {
        if (resp && resp.status === 422) {
          showToast(resp.description || 'That combination is sold out.');
          addBtn.removeAttribute('disabled');
          addBtn.textContent = origLabel;
          return;
        }
        // The re-rendered section already drops any card whose product is now in the cart,
        // so the old fade-out-then-hide dance on the source card is gone with it.
        closePicker();
        openDrawer();
        var summary = applyDrawerSection(resp && resp.sections && resp.sections[SECTION_ID]);
        if (summary) { settleGift(summary); return; }
        return refresh();
      }).catch(function () {
        showToast('Something went wrong. Try again.');
        addBtn.removeAttribute('disabled');
        addBtn.textContent = origLabel;
      });
    }
  });

  window.LV = window.LV || {};
  window.LV.openCart = function () { openDrawer(); if (cartCache) renderCart(cartCache); return refresh(); };

  // Initial sync -- prefetch cart so first ATC + first open are instant
  refresh();
})();
